import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';

/** A voided bill row as returned by ezydine.admin_list_voided_orders(). */
interface AdminVoidedOrder {
  id: string;
  restaurantId: string;
  restaurantName: string;
  billNo: string | null;
  type: string;
  customer: string;
  total: number;
  voidedAt: string;
  voidedBy: string | null;
  voidReason: string | null;
  placedAt: string;
}

const RETENTION_DAYS = 60; // mirrors _retention_days in ezydine-void-retention.sql

/** Cross-tenant recycle bin: every restaurant's voided bills, for platform
 *  support. Reads/restores go through SECURITY DEFINER RPCs gated on
 *  ezypms.is_super_admin() — see ezydine-void-retention-superadmin.sql. */
export default function SuperadminVoided() {
  const [rows, setRows] = useState<AdminVoidedOrder[] | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_voided_orders', { p_limit: 1000 });
      if (error) throw error;
      setRows((data ?? []) as AdminVoidedOrder[]);
      setError('');
    } catch (e) {
      setError(explain(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const restore = useCallback(async (o: AdminVoidedOrder) => {
    const label = o.billNo || `₹${o.total.toFixed(0)}`;
    if (!window.confirm(`Restore ${label} for ${o.restaurantName}? It goes back into their sales and POS.`)) return;
    setBusyId(o.id);
    try {
      const { error } = await supabase.rpc('admin_restore_bill', { p_order: o.id });
      if (error) throw error;
      setRows((prev) => (prev ? prev.filter((x) => x.id !== o.id) : prev));
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusyId(null);
    }
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows ?? [];
    return (rows ?? []).filter(
      (o) =>
        o.restaurantName.toLowerCase().includes(q) ||
        (o.billNo ?? '').toLowerCase().includes(q) ||
        o.customer.toLowerCase().includes(q) ||
        (o.voidedBy ?? '').toLowerCase().includes(q) ||
        (o.voidReason ?? '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  const loading = rows === null && !error;

  return (
    <div className="p-6 space-y-5">
      <div>
        <h2 className="text-lg font-bold text-foreground-800">Voided Bills</h2>
        <p className="text-sm text-foreground-500 mt-0.5">
          Bills voided on any tenant's POS. Restore one to put it back into that restaurant's sales. Rows are
          permanently purged {RETENTION_DAYS} days after they were voided.
        </p>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
        <input
          type="text"
          placeholder="Search restaurant, bill no, customer, who voided, reason…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-96"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
          <button onClick={load} className="ml-3 underline hover:no-underline cursor-pointer">
            Retry
          </button>
        </div>
      )}

      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 border-b border-background-200">
              <tr>
                {['Restaurant', 'Bill No', 'Type', 'Customer', 'Amount', 'Voided by', 'Reason', 'Voided on', 'Purges in', ''].map(
                  (h, i) => (
                    <th
                      key={h || i}
                      className={`px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap ${
                        h === 'Amount' ? 'text-right' : 'text-left'
                      }`}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-background-200">
              {loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-foreground-400">
                    Loading voided bills…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-foreground-400">
                    {search.trim() ? 'No voided bill matches your search.' : 'No voided bills across any tenant.'}
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3 font-medium text-foreground-800 whitespace-nowrap">{o.restaurantName}</td>
                  <td className="px-4 py-3 font-semibold text-foreground-700 whitespace-nowrap">{o.billNo || '—'}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.type}</td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.customer || '—'}</td>
                  <td className="px-4 py-3 text-right font-medium text-foreground-800 whitespace-nowrap">
                    ₹{Number(o.total).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{o.voidedBy || '—'}</td>
                  <td className="px-4 py-3 text-foreground-600 max-w-[14rem] truncate" title={o.voidReason ?? ''}>
                    {o.voidReason || '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{fmtDate(o.voidedAt)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <PurgeCountdown voidedAt={o.voidedAt} />
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => restore(o)}
                      disabled={busyId === o.id}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-primary-300 text-primary-600 hover:bg-primary-50 disabled:opacity-50 cursor-pointer"
                    >
                      <i className="ri-arrow-go-back-line"></i>
                      {busyId === o.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function PurgeCountdown({ voidedAt }: { voidedAt: string }) {
  const purgeAt = new Date(voidedAt).getTime() + RETENTION_DAYS * 86_400_000;
  const daysLeft = Math.ceil((purgeAt - Date.now()) / 86_400_000);
  if (daysLeft <= 0) return <span className="text-red-600 font-medium">due for removal</span>;
  return (
    <span className={daysLeft <= 7 ? 'text-red-600 font-medium' : 'text-foreground-500'}>
      {daysLeft} day{daysLeft === 1 ? '' : 's'}
    </span>
  );
}

/** Turn RPC errors into something actionable — the usual cause is the migration
 *  not being applied to this project yet. */
function explain(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e);
  const m = msg.toLowerCase();
  if (m.includes('function') && (m.includes('does not exist') || m.includes('not find'))) {
    return 'Voided-bills RPCs are missing. Run ezydine-void-retention.sql and ezydine-void-retention-superadmin.sql in the SQL editor.';
  }
  if (m.includes('forbidden')) {
    return 'Your admin email is not in ezypms.platform_admins, so cross-tenant access is denied.';
  }
  return msg;
}
